import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-new-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    RouterModule
  ],
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.css']
})
export class NewPasswordComponent implements OnInit {
  resetPasswordForm: FormGroup;
  token: string = '';
  email: string = '';
  message: string = '';

  error: any;
  loading = false;

  passwordStrength = 0;
  isTyping = false; // progress bar-ის გამოჩენა მხოლოდ აკრეფისას

  showPassword = false;
  showConfirmPassword = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) {
    this.resetPasswordForm = this.fb.group({
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/.*[a-z].*/), // მინიმუმ 1 პატარა ასო
        Validators.pattern(/.*\d.*/)    // მინიმუმ 1 ციფრი
      ]],
      password_confirmation: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });

    // ლაივ რეჟიმში პაროლების შემოწმება
    this.resetPasswordForm.valueChanges.subscribe(() => {
      this.resetPasswordForm.get('password_confirmation')?.updateValueAndValidity();
    });

    // პაროლის სიძლიერის გამოსათვლელად მონიტორინგი
    this.resetPasswordForm.get('password')?.valueChanges.subscribe(value => {
      this.isTyping = value.length > 0; // progress bar-ის გამოჩენა მხოლოდ აკრეფისას
      this.calculateStrength(value);
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      this.email = params['email'];

      if (!this.token || !this.email) {
        this.message = 'Invalid password reset link!';
      }
    });
  }

  resetPassword() {
    if (this.resetPasswordForm.valid) {
      const payload = {
        email: this.email,
        token: this.token,
        password: this.resetPasswordForm.value.password,
        password_confirmation: this.resetPasswordForm.value.password_confirmation
      };
      this.loading = true;
      this.http.post('https://menuload.com/api/auth/new/password', payload)
        .subscribe(() => {
          this.loading = false;
          this.toastrService.success(this.translateService.instant('PASSWORD_UPDATED_SUCCESS'));
          setTimeout(() => this.router.navigate(['/']), 3000);
        }, () => {
          this.loading = false;
          this.toastrService.error(this.translateService.instant('PASSWORD_UPDATED_ERROR'));
        });
    }
  }

  calculateStrength(password: string) {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;

    this.passwordStrength = (strength / 4) * 100;
  }

  hasLowerCase(): boolean {
    return /[a-z]/.test(this.resetPasswordForm.get('password')?.value || '');
  }

  hasNumber(): boolean {
    return /\d/.test(this.resetPasswordForm.get('password')?.value || '');
  }

  // პაროლების დამთხვევის ვალიდატორი
  passwordMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
    const password = formGroup.get('password');
    const confirmPassword = formGroup.get('password_confirmation');
  
    if (!password || !confirmPassword) return null;
  
    if (confirmPassword.errors && !confirmPassword.errors['passwordMismatch']) {
      return null; // სხვა ვალიდაციების დატოვება
    }
  
    if (password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
    } else {
      confirmPassword.setErrors(null);
    }
    
    return password.value !== confirmPassword.value ? { passwordMismatch: true } : null;
  }

  get passwordControl() {
  return this.resetPasswordForm.get('password');
}

get confirmationControl() {
  return this.resetPasswordForm.get('password_confirmation');
}

hasMinLength(): boolean {
  const value = this.passwordControl?.value || '';
  return value.length >= 8;
}

hasUpperCase(): boolean {
  return /[A-Z]/.test(this.passwordControl?.value || '');
}

}